import React from 'react';
import gettext from '@eb/gettext';
import { SubmissionError } from 'redux-form';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';

import { trackEventFromState } from '@eb/site-analytics';
import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
} from '@eb/social-login-facebook';

import {
    sendResetPasswordEmail,
    sendSoftAccountActivationEmail,
    setAccountExistent,
    updateFormType,
    updateCheckoutFormType,
    showAuthNotification,
    existingSoftAccountCheck,
} from './auth';
import { socialLogInSoftUser } from './social';

import { trackSIWA_EmailNotFound } from './gaTracking';
import {
    hasFbAndAppleAccountOnly,
    hasFbAccount,
    hasAppleAccount,
} from './lookup';

import {
    BASE_URL,
    GA_AUTH_CATEGORY,
    GA_AUTH_CHECKOUT_CATEGORY,
    GA_ENTER_EMAIL_EVENT,
    GA_EXISTING_ACCT_LABEL,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
    GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE_FB,
    GA_NEW_ACCT_LABEL,
    GA_SOFT_ACCT_LABEL,
    LOG_IN_PATH,
    ONBOARDING_REDIRECT_URL_BLACKLIST,
    SIGN_UP_PATH,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LOOK_UP_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
} from '../constants';
import {
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
    SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
    EMAIL_UNEXISTENT_ERROR_MESSAGE,
    EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
    EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
    ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
} from '../constants/messages';

import { notifyParent } from '../utils';

import { AlertChunky } from '@eb/eds-iconography';

export const getLogInFormData = (
    {
        email: formEmail,
        password,
        captcha: recaptchaResponse,
        recaptchaV3ResponseToken,
    },
    getState,
) => {
    const {
        authentication: { email, forward, pckg, stld },
    } = getState();

    let {
        authentication: { referrer },
    } = getState();

    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer');
    }

    return {
        password,
        email: formEmail || email,
        forward,
        referrer,
        pckg,
        stld,
        recaptchaResponse,
        recaptchaV3ResponseToken,
    };
};

export const getSignUpFormData = (
    {
        firstName,
        lastName,
        password,
        captcha: recaptchaResponse,
        email: formEmail,
    },
    getState,
) => {
    const {
        authentication: { email, forward, referrer, userTypeParams, pckg },
    } = getState();
    const _email = formEmail || email;

    return {
        password,
        firstName,
        lastName,
        recaptchaResponse,
        email: _email,
        forward,
        referrer,
        ...userTypeParams,
        pckg,
    };
};

export const getSocialLogInFormData = (
    { accessToken, sourceProvider, skipCreation },
    getState,
) => {
    const {
        authentication: { forward, pckg },
    } = getState();

    let {
        authentication: { referrer },
    } = getState();

    if (global.window && window.localStorage) {
        referrer = localStorage.getItem('referrer');
        localStorage.removeItem('referrer');
    }

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        forward,
        referrer,
        pckg,
    };
};

export const getSocialSignUpFormData = (
    { email, firstName, lastName },
    currentState,
) => {
    const skipCreation = false;
    const {
        authentication: {
            forward,
            referrer,
            pckg,
            socialData: { accessToken, sourceProvider },
        },
    } = currentState;

    return {
        accessToken,
        sourceProvider,
        skipCreation,
        email,
        firstName,
        lastName,
        forward,
        referrer,
        pckg,
    };
};

export const handleResetPasswordAccountStatusResponse = (
    dispatch,
    { exists, can_login: canLogin },
) => {
    let emailAction = sendResetPasswordEmail;

    if (exists && !canLogin) {
        emailAction = sendSoftAccountActivationEmail;
    }

    if (!exists && !canLogin) {
        throw new SubmissionError({ email: gettext('Unknown email provided') });
    }

    return dispatch(emailAction());
};

export const handleLookupUserResponse = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_EXISTING_ACCT_LABEL,
        });
        dispatch(updateFormType(LOG_IN_PATH));
    } else if (
        hasFbAndAppleAccountOnly({ socialSSOSettings, userId, signInMethods })
    ) {
        trackEventFromState(state, {
            action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE_FB,
            category: GA_AUTH_CATEGORY,
        });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (hasFbAccount({ socialSSOSettings, userId, signInMethods })) {
        trackEventFromState(state, {
            action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_FB,
            category: GA_AUTH_CATEGORY,
        });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (hasAppleAccount({ socialSSOSettings, userId, signInMethods })) {
        trackEventFromState(state, {
            action: GA_GET_STARTED_EMAIL_ALREADY_CONNECTED_TO_APPLE,
            category: GA_AUTH_CATEGORY,
        });
        dispatch(
            showAuthNotification({
                message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (userId) {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_SOFT_ACCT_LABEL,
        });
        dispatch(sendSoftAccountActivationEmail());
    } else {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CATEGORY,
            label: GA_NEW_ACCT_LABEL,
        });
        dispatch(updateFormType(SIGN_UP_PATH));
    }
};

export const handleLookupUserResponseForCheckout = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        authentication: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CHECKOUT_CATEGORY,
            label: GA_EXISTING_ACCT_LABEL,
        });
        dispatch(updateCheckoutFormType(LOG_IN_PATH));
    } else if (hasFbAccount({ socialSSOSettings, userId, signInMethods })) {
        dispatch(updateCheckoutFormType(SOCIAL_LOG_IN_PATH));
    } else if (userId) {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CHECKOUT_CATEGORY,
            label: GA_SOFT_ACCT_LABEL,
        });
        dispatch(
            showAuthNotification({
                message: EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
        dispatch(existingSoftAccountCheck({ userHasSoftAccount: true }));
        notifyParent('checkoutLoginSoftAccount');
    } else {
        trackEventFromState(state, {
            action: GA_ENTER_EMAIL_EVENT,
            category: GA_AUTH_CHECKOUT_CATEGORY,
            label: GA_NEW_ACCT_LABEL,
        });
        dispatch(
            showAuthNotification({
                message: EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    }
};

export const handleLookupUserResponseForLinking = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: { pathname },
        },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (socialLogin) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (
        isUndefined(userId) &&
        pathname === `${BASE_URL}/${SOCIAL_LOOK_UP_PATH}`
    ) {
        trackSIWA_EmailNotFound(state);
        dispatch(
            showAuthNotification({
                message: EMAIL_UNEXISTENT_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (
        !!userId &&
        includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN)
    ) {
        // for now, we are asking for EB account linking only in the SignIn with Apple flow,
        // in the future, when we ask for linking for other providers we should redefine this condition
        dispatch(
            showAuthNotification({
                message: SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        dispatch(updateFormType(SOCIAL_LOG_IN_PATH));
    } else if (
        !!userId &&
        isEqual(signInMethods, [SIGN_IN_METHOD_FACEBOOK_LOGIN])
    ) {
        // if only has facebook as login method, we cannot link their account asking for email+password
        dispatch(
            showAuthNotification({
                message: ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
                iconType: <AlertChunky />,
            }),
        );
    } else if (!!userId && isEmpty(signInMethods)) {
        dispatch(socialLogInSoftUser());
        dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));
    }
};

export const shouldOnboardingRedirect = (state) => {
    const referrer = state.authentication.referrer;

    return !find(ONBOARDING_REDIRECT_URL_BLACKLIST, (blacklistedPath) =>
        referrer.includes(blacklistedPath),
    );
};
